import { differenceInYears } from 'date-fns';

import type {
  GraphQLCommitment,
  GraphQLDeparture,
  GraphQLDepartureChild,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { toIsoDateString } from '@sola/utils/date.ts';

import { parseCalendarDate } from '../date.ts';

export const DEPARTURE_LOOKBACK_DAYS = 75;
export const DEPARTURE_TRANSITION_WINDOW_DAYS = 10;

const MILLISECONDS_PER_DAY = 86_400_000;

/**
 * Checks whether a commitment belongs to a departed child.
 */
export const isDepartureCommitment = (
  commitment: GraphQLCommitment,
  child: GraphQLDepartureChild,
): boolean =>
  commitment.internalId === child.commitmentInternalId ||
  commitment.participant?.globalId === child.globalId;

/**
 * Finds the commitment associated with a departed child.
 */
export const findDepartureCommitment = (
  commitments: GraphQLCommitment[],
  child: GraphQLDepartureChild,
): GraphQLCommitment | undefined =>
  commitments.find((commitment) => isDepartureCommitment(commitment, child));

export const getCompletedYears = (
  startDateValue?: string | null,
  endDateValue?: string | null,
): number | undefined => {
  const startDate = parseCalendarDate(startDateValue ?? undefined);
  const endDate = parseCalendarDate(endDateValue ?? undefined);

  if (!startDate || !endDate || endDate < startDate) {
    return undefined;
  }

  return differenceInYears(endDate, startDate);
};

/**
 * Returns the completed sponsorship years for a departure.
 */
export const getSponsorYears = (
  departure: GraphQLDeparture,
  sponsorships: GraphQLCommitment[],
): number | undefined => {
  const commitment = findDepartureCommitment(
    sponsorships,
    departure.departedChild,
  );

  return getCompletedYears(
    commitment?.dates.start,
    commitment?.dates.end ?? departure.effectiveDate,
  );
};

/**
 * Returns the UTC start-of-day timestamp for a date.
 */
const getUtcDateStart = (value: string | Date): number | null => {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/**
 * Returns the number of days since a departure took effect.
 */
export const getDepartureAgeInDays = (
  effectiveDate: string,
  referenceDate: Date = new Date(),
): number | null => {
  const effectiveDateStart = getUtcDateStart(effectiveDate);
  const referenceDateStart = getUtcDateStart(referenceDate);

  if (effectiveDateStart === null || referenceDateStart === null) {
    return null;
  }

  return Math.floor(
    (referenceDateStart - effectiveDateStart) / MILLISECONDS_PER_DAY,
  );
};

/**
 * Checks whether a departure is still in its transition window.
 */
export const isInDepartureTransitionWindow = (
  effectiveDate: string,
  referenceDate: Date = new Date(),
): boolean => {
  const ageInDays = getDepartureAgeInDays(effectiveDate, referenceDate);

  return ageInDays !== null && ageInDays < DEPARTURE_TRANSITION_WINDOW_DAYS;
};

/**
 * Checks whether a departure is ready for supporter action.
 */
export const isActionableDeparture = (
  effectiveDate: string,
  referenceDate: Date = new Date(),
): boolean => {
  const ageInDays = getDepartureAgeInDays(effectiveDate, referenceDate);

  return (
    ageInDays !== null &&
    ageInDays >= DEPARTURE_TRANSITION_WINDOW_DAYS &&
    ageInDays <= DEPARTURE_LOOKBACK_DAYS
  );
};

/**
 * Returns the earliest departure date included in the lookback window.
 */
export const getDepartureLookbackStartDate = (
  referenceDate: Date = new Date(),
): string => {
  const lookbackStart = new Date(referenceDate);
  lookbackStart.setUTCDate(
    lookbackStart.getUTCDate() - DEPARTURE_LOOKBACK_DAYS,
  );

  return toIsoDateString(lookbackStart)!;
};
